<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import axios from "axios";

import useNotify from "@Composables/useNotify";
import { VueECharts as VChart } from "@Helpers/echarts";
import useTranslation from "@Composables/useTranslation";
import { useHandleResize, ResizableChart } from "@Composables/useHandleResize";
import WorldTrafficStats from "@Modules/analytics/DTOs/WorldTrafficStats";
import { countryCodeToCountry, countryToCode } from "@Modules/analytics/Helpers/CountryCodeToCountry";
import ChartLayout from "@Modules/analytics/Components/ChartLayout.vue";

import { ValidWorldMapTypes } from "./WorldTrafficChart/WorldTraffic";

//
// Props
interface Props {
  dataset?: WorldTrafficStats;
  loading?: boolean;
  debug?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  // FIXME: add isloading
    dataset: () => new WorldTrafficStats(),
    loading: false,
    debug: false,
});

//
// Emit
const emit = defineEmits<{ (e: "map-change", map: ValidWorldMapTypes): void }>();

//
// Setup
const { notifyError } = useNotify();
const { __ } = useTranslation();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Data
const data = reactive<{
    mapType: ValidWorldMapTypes,
    loading: boolean,
    loaded: Record<string, boolean>,
    hasDetails: ValidWorldMapTypes[]
}>({
    mapType: "world",
    loading: false,
    loaded: {
        "world": true,
    },
    hasDetails: ["au", "ca", "cn", "in", "ru", "ua", "us", "mx"],
});
const chart = ref<ResizableChart | null>(null);

useHandleResize(chart);

//
// Computed
const chartData = computed(() => props.dataset.getChartData());
const mapData = computed(() => Object.values(props.dataset.getRawData() || []).map((item) => ({ name: countryCodeToCountry(item.name), value: item.value })));
const total = computed(() => props.dataset.getChartTotal());
const hasChartData = computed(() => chartData.value.length > 0);

const limitValues = computed<{
    min: number,
    max: number
}>(() => {
    let minValue = 0;
    let maxValue = 0;

    if (mapData.value.length > 0) {
        minValue = maxValue = mapData.value[0]?.value || 0;

        for (let i = 1; i < mapData.value.length; i++) {
            const val = mapData.value[i]?.value;

            if (val) {
                minValue = Math.min(minValue, val);
                maxValue = Math.max(maxValue, val);
            }
        }

        if (minValue > 0 && minValue === maxValue) {
            minValue = 0;
        }
    } else {
        minValue = 0;
        maxValue = total.value;
    }

    return {
        min: minValue,
        max: maxValue,
    };
});
const minValue = computed(() => limitValues.value.min || 0);
const maxValue = computed(() => limitValues.value.max || 0);

//
// Hooks
onMounted(() => {
    setMap("world");
});

const title = computed(() => {
    const key = "countries:country." + data.mapType.toUpperCase();
    const title = __(key, {
        returnObjects: true,
        defaultValue: data.mapType,
    });

    return __("chart.title.users_map", {
        type: Array.isArray(title) ? title.pop() : title,
    });
});

const mapOptions = computed(() => {

    if (!data.mapType) return {};

    return {
        toolbox: {
            show: true,
            left: "left",
            feature: {
                myHome: {
                    show: data.mapType !== "world",
                    title: __("chart.back_to_world"),
                    icon: "image:///images/globe.png",
                    onclick: () => {
                        emit("map-change", "world");
                    },
                },
                saveAsImage: { show: true, title: __("chart.save_as_image") },
            },
        },
        tooltip: {
            trigger: "item",
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- echarts v4 formatter callback params are untyped
            formatter: (params: any) => {
                const percent = Math.round(((params.value * 100) / total.value || 0) * 100) / 100;

                const content =
                    trans(params.name) +
                    "<br />" +
                    __("chart.clicks") +
                    ": " +
                    parseInt(params.value || 0).toLocaleString() +
                    " ( " +
                    percent +
                    " % )";
                return content;
            },
        },
        visualMap: {
            min: minValue.value,
            max: maxValue.value,
            text: [__("chart.high"), __("chart.low")],
            calculable: true,
            realtime: false,
            inRange: {
                color: [
                    "rgb(255, 255, 217)",
                    "rgb(189, 229, 181)",
                    "rgb(42, 154, 192)",
                    "rgb(28, 48, 131)",
                ],
            },
        },
        series: [
            {
                type: "map",
                mapType: data.mapType,
                roam: false,
                aspectScale: 0.75,
                zoom: 1.3,
                label: {
                    normal: {
                        show: false,
                    },
                    emphasis: {
                        show: false,
                    },
                },
                itemStyle: {
                    normal: {
                        borderColor: "rgba(0, 0, 0, 0.2)",
                    },
                    emphasis: {
                        areaColor: null,
                        shadowOffsetX: 0,
                        shadowOffsetY: 0,
                        shadowBlur: 10,
                        borderWidth: 0,
                        shadowColor: "rgba(0, 0, 0, 0.5)",
                    },
                },
                data: mapData.value || [],
            },
        ],
    };
});

const chartOptions = computed(() => {
    return {
        title: {
            text: __("chart.title.top_sources"),
            left: "center",
            top: "top",
            show: false,
        },
        toolbox: {
            show: false,
            feature: {
                saveAsImage: { show: true, title: __("chart.save_as_image") },
            },
        },
        grid: {
            left: "10%",
            top: "0",
            right: "10%",
            bottom: "6%",
        },
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "none",
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- echarts v4 formatter callback params are untyped
            formatter: (params: any) => {
                const persent =
                    Math.round(((params[0].value * 100) / total.value || 0) * 100) /
                    100;

                const content =
                    trans(params[0].name) +
                    "<br />" +
                    __("chart.clicks") +
                    ": " +
                    parseInt(params[0].value).toLocaleString() +
                    " ( " +
                    persent +
                    " % )";
                return content;
            },
        },
        xAxis: [
            {
                inverse: true,
                type: "value",
                axisTick: {
                    show: false,
                },
                axisLine: {
                    show: false,
                },
                splitNumber: 3,
                axisLabel: {
                    fontWeight: "bold",
                    formatter: (value: number) => {
                        let persent = (value / total.value) * 100;
                        persent = persent || 0;
                        return Math.round((persent * 100) / 100).toLocaleString() + "%";
                    },
                },
            },
        ],
        yAxis: [
            {
                type: "category",
                data: props.dataset.getChartLabels(),

                axisTick: {
                    show: false,
                },
                axisLabel: {
                    show: true,
                    inside: true,
                    fontWeight: "bold",
                },
                axisLine: {
                    show: false,
                },
            },
        ],
        series: [
            {
                id: "clicks",
                type: "bar",
                name: __("chart.clicks"),
                itemStyle: {
                    normal: {
                        color: "rgba(51, 152, 219, 0.2)",
                    },
                    emphasis: {
                        color: "rgba(51, 152, 219, 0.4)",
                    },
                },
                label: {
                    normal: {
                        show: true,
                        color: "rgb(51, 138, 207)",
                        fontWeight: "bold",
                        position: "insideRight",
                        formatter: (params: { value: number }) => {
                            let persent = (params.value / total.value) * 100;
                            persent = persent || 0;
                            return (
                                (Math.round(persent * 100) / 100).toLocaleString() + "%"
                            );
                        },
                        fontSize: 14,
                    },
                },
                data: chartData.value || [],
            },
        ],
    };
});

const trans = function (name: string) {
    const nameTemplate =
        "countries:" +
        (data.mapType === "world" ? "country" : "state." + data.mapType) +
        "." +
        name.toUpperCase();

    const code = (data.mapType === "world" ? "" : data.mapType + "-") + name;
    const title = __(nameTemplate, {
        returnObjects: true,
        defaultValue: code,
    });

    return Array.isArray(title) ? title.pop() : title;
};

const setMap = function (map: ValidWorldMapTypes) {

    if (!data.loaded[map]) {
        data.loading = true;

        axios
            .get("/maps/" + map + ".json")
            .then((response) => {
                VChart.registerMap(map, response.data);
                data.loaded[map] = true;
                data.mapType = map;
            })
            .catch(() => {
                // if (this.isUserAuthorized("supervisor")) {
                notifyError({
                    text: __("chart.map_not_found", { map: map }),
                });
                // }

                /**
                 * The parent has already swapped the dataset to the requested map, so leaving
                 * mapType behind would render country-scoped data on the previous map. Fall
                 * back to the world view, which is always registered, to keep the two in sync.
                 */
                if (map !== "world") {
                    emit("map-change", "world");
                }
            })
            .finally(() => {
                data.loading = false;
            });
    } else {
        data.mapType = map;
    }
};

/**
 * Narrows an arbitrary country code to a map we actually ship a drill-down for.
 */
const isDrillableMap = function (map: string): map is ValidWorldMapTypes {
    return data.hasDetails.some((valid) => valid === map);
};

const mapClicked = function (e: { name: string }) {
    const theCountry = countryToCode(e.name)?.toLowerCase();
    if (
        data.mapType === "world" &&
        theCountry &&
        isDrillableMap(theCountry)
    ) {
        emit("map-change", theCountry);
    }
};

//
// Watchers
watch(
    () => props.dataset.map,
    (newMap) => {
        if (newMap !== data.mapType) {
            setMap(newMap);
        }
    },
);

watch(hasChartData, () => {
    nextTick(() => {
        chart.value?.resize();
    });
});

//
// Exposed Test Vars
defineExpose({ data, mapClicked, setMap });
</script>

<template>
  <ChartLayout
    class="stats-world-traffic"
    :class="{ 'stats-world-traffic--mobile': isMobileView }"
    :is-loading="loading"
  >
    <template #header>
      {{ title }}
    </template>
    <div class="stats-world-traffic__charts">
      <v-chart
        ref="chart"
        class="stats-world-traffic__map"
        :options="mapOptions"
        @click="mapClicked"
      />
      <v-chart
        v-if="hasChartData"
        ref="locations-chart"
        class="stats-world-traffic__vertical-chart"
        :options="chartOptions"
        @click="mapClicked"
      />
    </div>
    <!--
      The maps are drawn onto a canvas, so there is no per-country element for
      automated tests to click. In debug mode only, expose the same drill-down
      the map click performs as real buttons.
    -->
    <div
      v-if="debug"
      class="stats-world-traffic__debug-drill"
      data-test-id="world-map-drill"
    >
      <button
        v-for="code in data.hasDetails"
        :key="code"
        type="button"
        :data-test-id="`world-map-drill-${code}`"
        @click="emit('map-change', code)"
      >
        {{ code }}
      </button>
      <button
        type="button"
        data-test-id="world-map-drill-world"
        @click="emit('map-change', 'world')"
      >
        world
      </button>
    </div>
  </ChartLayout>
</template>

<style lang="scss" scoped>
.stats-world-traffic {
  $self: &;

  &__charts {
    display: flex;
  }

  &__map {
    flex: 3;
  }

  &__vertical-chart {
    flex: 1;
  }

  &__map,
  &__vertical-chart {
    width: 100%;
  }

  &--mobile {
    #{ $self }__map {
      flex: 1;
    }
  }
}
</style>
